// --- React ---
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// --- Services ---
import { useUnsavedChangesWarning } from "../../../hooks/useUnsavedChangesWarning";
import parameterSvc from "../../../services/api/parameter";

// --- Components ---
import ModalConfirm from "../../../components/modal-comfirm";
import Loading from "../../../components/loading/loading";
import FormInput from "../../../components/form-input/FormInput";
import { notify } from "../../../common/notificationService";
import { omitMetaFields } from "../../../utils/helper";
import ButtonGoback from "../../../components/button/buttonGoback";

// --- Models ---
import { createParameterSchema, updateParameterSchema } from "../../../models/schema/parameter";
import { Parameter } from "../../../models/types/parameter";

// --- Utils ---
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { LoaderCircle } from "lucide-react";
import { UrlCollection } from "../../../common/url-collection";
import { useLookupParameterUnit } from "../../../utils/get-lookup";
import FormSelect from "../../../components/form-input/formSelect";
import FormInputSpecialChar from "../../../components/form-input/FormInputSpecialChar";
import { useTranslation } from "react-i18next";

interface FormProps {
  isCreate: boolean;
  data: Parameter | null;
}

export default function ParameterCreateUpdate({ isCreate, data }: FormProps) {
  const navigate = useNavigate();
  const { lookupParameterUnit } = useLookupParameterUnit();

  const [loading, setLoading] = useState(false);
  const { t } = useTranslation()

  const schema = isCreate ? createParameterSchema : updateParameterSchema;

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      code: '',
      description: '',
      unitId: 0,
    },
  });

  useEffect(() => {
    if (data) {
      reset(omitMetaFields(data));
    }
  }, [data]);

  const onSubmit = async (values: any) => {
    setLoading(true);
    try {
      const res = isCreate ? await parameterSvc.createParameter(values) : await parameterSvc.updateParameter({ ...values, id: Number(data?.id) });
      if (res && res.content.status) {
        unlock();
        notify(t("common.success"), isCreate ? t("parameter.form.createTitle") : t("parameter.form.updateTitle"), "success");
        setLoading(false);
        navigate(UrlCollection.Parameter);
      }
    } catch (e: any) {
      notify(t("common.error"), t("common.errorMessage"), "danger");
    } finally {
      setLoading(false);
    }
  };

  const { showModal, confirmLeave, cancelLeave, confirmContent, unlock } = useUnsavedChangesWarning(isDirty);

  return (
    <>
      <ModalConfirm
        isOpen={showModal}
        onClose={cancelLeave}
        title={confirmContent.title}
        content={confirmContent.content}
        titleComfirm={t("common.leavePage")}
        action={confirmLeave}
      />
      <div className="w-full h-full">
        <ButtonGoback />
        <div className="max-w-md mx-auto p-8 bg-white rounded-lg shadow mt-3">
          <h2 className="text-2xl font-bold mb-6 text-center">{isCreate ? t("parameter.form.createTitle") : t("parameter.form.updateTitle")}</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-4">
              <FormInputSpecialChar
                label={t("parameter.form.fields.name")}
                name="name"
                register={register("name")}
                error={errors.name}
                required
                setValue={setValue}
                watch={watch}
              />
              <FormInputSpecialChar
                label={t("parameter.form.fields.code")}
                name="code"
                register={register("code")}
                error={errors.code}
                required
                setValue={setValue}
                watch={watch}
              />
              <FormInputSpecialChar
                label={t("parameter.form.fields.description")}
                name="description"
                register={register("description")}
                error={errors.description}
                setValue={setValue}
                watch={watch}
              />
              <FormSelect
                label={t("parameter.form.fields.unit")}
                name="unitId"
                control={control}
                options={lookupParameterUnit}
                required
              />
              {/* <FormInput
              label={t("parameter.form.field.defaultValue")}
              name="defaultValue"
              type="text"
              inputMode="decimal"
              register={register("defaultValue", {
                validate: value =>
                  /^-?\d*(\.\d+)?$/.test(value) || "Vui lòng nhập số thập phân hợp lệ"
              })}
              error={errors.defaultValue}
            /> */}
            </div>
            <button
              disabled={!isDirty}
              type="submit"
              className="btn_submit w-full mt-4"
            >
              <div className="flex items-center justify-center gap-2">
                {loading ? <LoaderCircle /> : ""}
                {isCreate ? t("parameter.form.buttons.create") : t("parameter.form.buttons.update")}
              </div>
            </button>
          </form>
        </div>
      </div>
    </>
  );
}